import React, { useState } from "react";
import { Droppable } from "@hello-pangea/dnd";
import { AlertTriangle, Plus, ChevronDown, ChevronUp } from "lucide-react";
import InfoItemRow from "./InfoItemRow";
import { colors, spacing, borderRadius, shadows } from "../styles/globalStyles";
import { useLanguage } from "../contexts/LanguageContext";

const SectionCard = ({
  section,
  sectionIndex,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onToggleSectionVisibility,
  onMoveItem,
  onSaved,
  suggestions = [],
  defaultExpanded = true,
}) => {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const getSectionBgColor = (color) => {
    const colorMap = {
      red: '#fef2f2',
      orange: '#fff7ed',
      teal: '#f0fdfa',
      blue: '#eff6ff',
      gray: colors.gray[50]
    };
    return colorMap[color] || colorMap.gray;
  };

  const getSectionBorderColor = (color) => {
    const colorMap = {
      red: '#fecaca',
      orange: '#fed7aa',
      teal: '#99f6e4',
      blue: '#bfdbfe',
      gray: colors.gray[300]
    };
    return colorMap[color] || colorMap.gray;
  };

  const getHeaderBgColor = (color) => {
    const colorMap = {
      red: colors.danger,
      orange: colors.warning,
      teal: colors.secondary,
      blue: colors.medical.blue,
      gray: colors.gray[600]
    };
    return colorMap[color] || colorMap.gray;
  };

  const handleAddItem = async () => {
    if (!newKey.trim() || !newValue.trim()) {
      setErrorMessage("Please fill in both field name and value");
      setTimeout(() => setErrorMessage(null), 5000);
      return;
    }

    try {
      setErrorMessage(null);
      await onAddItem(section.category, newKey.trim(), newValue.trim());
      setNewKey("");
      setNewValue("");
      setIsAddingItem(false);
      setShowSuggestions(false);
      onSaved?.();
    } catch (error) {
      // Clean error message - remove URLs/IPs
      let cleanError = "Failed to add item. Please try again.";
      if (error?.response?.data?.message) {
        cleanError = error.response.data.message;
        cleanError = cleanError.replace(/https?:\/\/[^\s]+/gi, "");
        cleanError = cleanError.replace(/\d+\.\d+\.\d+\.\d+/g, "");
        cleanError = cleanError.trim() || "Failed to add item. Please try again.";
      }
      setErrorMessage(cleanError);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const getInputPropsForKey = (key) => {
    const k = (key || "").toLowerCase();
    if (/\b(phone|mobile|contact|number)\b/.test(k)) return { type: "tel", autoComplete: "tel" };
    if (/\b(email|e-mail)\b/.test(k)) return { type: "email", autoComplete: "email" };
    if (/\b(age|weight|height)\b/.test(k)) return { type: "text", autoComplete: "off" };
    return { type: "text", autoComplete: "off" };
  };

  const handleSuggestionClick = (suggestion) => {
    setNewKey(suggestion);
    setIsAddingItem(true);
    setShowSuggestions(false);
  };

  const handleCancel = () => {
    setNewKey("");
    setNewValue("");
    setIsAddingItem(false);
    setShowSuggestions(false);
  };

  return (
    <div
      style={{
        marginBottom: spacing.lg,
        borderRadius: borderRadius.xl,
        border: `2px solid ${getSectionBorderColor(section.color)}`,
        backgroundColor: getSectionBgColor(section.color),
        boxShadow: shadows.soft,
        transition: "all 0.2s ease",
      }}
    >
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={`section-content-${section.id}`}
        style={{
          width: "100%",
          backgroundColor: getHeaderBgColor(section.color),
          color: colors.white,
          padding: spacing.lg,
          borderTopLeftRadius: borderRadius.xl,
          borderTopRightRadius: borderRadius.xl,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          minHeight: "48px",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
      >
        <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
          {section.icon && <span style={{ fontSize: "28px" }}>{section.icon}</span>}
          <div>
            <h3 style={{ fontSize: "18px", fontWeight: "700", margin: 0 }}>{section.name}</h3>
            <p style={{ fontSize: "12px", opacity: 0.9, margin: 0, marginTop: spacing.xs }}>
              {section.items.length} {section.items.length === 1 ? "item" : "items"}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
          <label
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: spacing.sm,
              cursor: "pointer",
              padding: "10px 12px",
              borderRadius: borderRadius.md,
              transition: "background-color 0.2s ease",
              minHeight: "44px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <input
              type="checkbox"
              checked={section.isVisible}
              onChange={(e) => onToggleSectionVisibility(section.id, e.target.checked)}
              style={{ width: "22px", height: "22px", cursor: "pointer" }}
              aria-label={section.isVisible ? "Section is public" : "Section is private"}
            />
            <span style={{ fontSize: "14px", fontWeight: "500", color: colors.white }}>
              {section.isVisible ? "Public" : "Private"}
            </span>
          </label>
          <span
            style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            aria-label={isExpanded ? "Collapse section" : "Expand section"}
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </span>
        </div>
      </button>

      <div
        id={`section-content-${section.id}`}
        style={{
          padding: spacing.lg,
          display: isExpanded ? "block" : "none",
          transition: "opacity 0.2s ease",
        }}
      >
        {/* Warning notice for sensitive sections */}
        {(section.category === "personal" || section.category === "identification") && (
          <div style={{
            marginBottom: spacing.lg,
            padding: spacing.md,
            backgroundColor: '#fef2f2',
            border: `2px solid ${colors.danger}`,
            borderRadius: borderRadius.lg,
            display: "flex",
            alignItems: "flex-start",
            gap: spacing.sm,
          }}>
            <AlertTriangle size={20} color={colors.danger} style={{ flexShrink: 0, marginTop: "2px" }} />
            <p style={{
              color: colors.danger,
              fontWeight: "600",
              fontSize: "13px",
              lineHeight: "1.6",
              margin: 0,
            }}>
              <strong>Important:</strong> This information will be visible to the public if visibility is set to public.
              Please use caution as you will be responsible for any sensitive information you share. We recommend keeping this
              section private unless absolutely necessary for emergency situations.
            </p>
          </div>
        )}

        <Droppable droppableId={section.category} type="ITEM">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                minHeight: "50px",
                backgroundColor: snapshot.isDraggingOver ? "rgba(59, 130, 246, 0.15)" : "transparent",
                border: snapshot.isDraggingOver ? "2px dashed #3b82f6" : "2px solid transparent",
                borderRadius: borderRadius.lg,
                padding: snapshot.isDraggingOver ? spacing.sm : 0,
                transition: "all 0.2s ease",
              }}
            >
              {section.items.length === 0 && !isAddingItem && (
                <p style={{
                  color: colors.gray[500],
                  textAlign: "center",
                  paddingTop: spacing.lg,
                  paddingBottom: spacing.lg,
                  fontStyle: "italic",
                  fontSize: "14px",
                }}>
                  {t("emergencyInfo.noItemsYet")}
                </p>
              )}

              {section.items.map((item, index) => (
                <InfoItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  onUpdate={(updates) => onUpdateItem(item.id, updates)}
                  onDelete={() => onDeleteItem(item.id)}
                  onMoveUp={index > 0 ? () => onMoveItem?.(section.category, index, "up") : undefined}
                  onMoveDown={index < section.items.length - 1 ? () => onMoveItem?.(section.category, index, "down") : undefined}
                  onSaved={onSaved}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        {isAddingItem && (
          <div style={{
            marginTop: spacing.md,
            padding: spacing.md,
            backgroundColor: colors.white,
            borderRadius: borderRadius.lg,
            border: `2px dashed ${colors.gray[300]}`,
          }}>
            {errorMessage && (
              <div
                style={{
                  marginBottom: spacing.sm,
                  padding: spacing.sm,
                  backgroundColor: '#fef2f2',
                  border: `1px solid ${colors.danger}`,
                  borderRadius: borderRadius.md,
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.xs,
                }}
              >
                <AlertTriangle size={16} color={colors.danger} />
                <p style={{ margin: 0, fontSize: "12px", color: colors.danger, fontWeight: "600" }}>{errorMessage}</p>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: spacing.sm }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: colors.gray[700],
                    marginBottom: spacing.xs,
                  }}
                >
                  Field Name <span style={{ color: colors.danger }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Field Name (e.g., Blood Group)"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  style={{
                    width: "100%",
                    padding: spacing.md,
                    minHeight: "44px",
                    border: `1px solid ${colors.gray[300]}`,
                    borderRadius: borderRadius.md,
                    fontSize: "16px",
                  }}
                  autoFocus
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: colors.gray[700],
                    marginBottom: spacing.xs,
                  }}
                >
                  Value <span style={{ color: colors.danger }}>*</span>
                </label>
                <input
                  {...getInputPropsForKey(newKey)}
                  placeholder="Value (e.g., O+)"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleAddItem();
                  }}
                  style={{
                    width: "100%",
                    padding: spacing.md,
                    minHeight: "44px",
                    border: `1px solid ${colors.gray[300]}`,
                    borderRadius: borderRadius.md,
                    fontSize: "16px",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: spacing.sm }}>
                <button
                  onClick={handleAddItem}
                  style={{
                    flex: 1,
                    backgroundColor: colors.success,
                    color: colors.white,
                    padding: spacing.md,
                    minHeight: "44px",
                    borderRadius: borderRadius.md,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "500",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.successDark)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.success)}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    backgroundColor: colors.gray[400],
                    color: colors.white,
                    padding: spacing.md,
                    minHeight: "44px",
                    borderRadius: borderRadius.md,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "500",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.gray[500])}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.gray[400])}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {!isAddingItem && (
          <div style={{ marginTop: spacing.md }}>
            <button
              onClick={() => {
                setIsAddingItem(true);
                setShowSuggestions(false);
              }}
              style={{
                width: "100%",
                backgroundColor: colors.medical.blue,
                color: colors.white,
                padding: spacing.md,
                minHeight: "48px",
                borderRadius: borderRadius.lg,
                border: "none",
                cursor: "pointer",
                fontWeight: "500",
                transition: "background-color 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: spacing.sm,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.medical.blueDark)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.medical.blue)}
              aria-label="Add new field to section"
            >
              <Plus size={20} />
              <span>{t("emergencyInfo.addField")}</span>
            </button>

            {showSuggestions && suggestions.length > 0 && (
              <div style={{
                marginTop: spacing.sm,
                padding: spacing.md,
                backgroundColor: colors.white,
                borderRadius: borderRadius.md,
                border: `1px solid ${colors.gray[300]}`,
              }}>
                <p style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colors.gray[700],
                  marginBottom: spacing.sm,
                }}>
                  Quick suggestions:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.sm }}>
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: `${spacing.xs} ${spacing.md}`,
                        backgroundColor: colors.gray[100],
                        borderRadius: borderRadius.md,
                        fontSize: "14px",
                        border: `1px solid ${colors.gray[300]}`,
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.gray[200]}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.gray[100]}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setIsAddingItem(true);
                    setShowSuggestions(false);
                  }}
                  style={{
                    marginTop: spacing.sm,
                    fontSize: "14px",
                    color: colors.medical.blue,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
                >
                  Or add custom field
                </button>
              </div>
            )}

            {showSuggestions && suggestions.length === 0 && (
              <button
                onClick={() => {
                  setIsAddingItem(true);
                  setShowSuggestions(false);
                }}
                style={{
                  marginTop: spacing.sm,
                  width: "100%",
                  fontSize: "14px",
                  color: colors.medical.blue,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "center",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
              >
                Add custom field
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionCard;
